import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Table,
  Badge,
  Button,
  Group,
  Text,
  Pagination,
  LoadingOverlay,
  Select,
  Alert,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  mvpApiListJobsInFlight,
  mvpApiListJobs,
  mvpApiListAllJobTasks,
  JobType,
  JobStatus,
  JobTaskStatus,
} from "@augmodo/lebron-browser-client";
import {
  mvpApiListStores,
  type StoreSchema as AtlasStoreSchema,
} from "@labeling-tools/api/atlas";
import {
  IconAdjustments,
  IconAlertCircle,
  IconCalendar,
  IconCircleCheck,
  IconPlayerPlay,
} from "@tabler/icons-react";
import { useGate } from "statsig-react";
import {
  Job,
  getStatusColor,
  getStatusText,
} from "@labeling-tools/ui-components/src/utils/helpers";
import { QaFeedbackTabContent } from "@/components/QaFeedbackTabContent";
import { MyTasksTabContent } from "@/components/MyTasksTabContent";
import { BenchmarkQuizCreateForm } from "@/components/BenchmarkQuizCreateForm";
import { QuizMetricsModal } from "@/components/QuizMetricsModal";
import { useMyBenchmarkQuizzes } from "@labeling-tools/api/lebron/react-query-hooks";
import { getCurrentUser } from "@/utils/auth";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setIsPlayMode, setTaskPlaylist } from "@/store";
import {
  PLAY_MODE_TABS,
  PLAY_MODE_JOB_TYPE,
  TASK_TYPE_ROUTE,
  BENCHMARK_JOB_TYPE_ROUTE,
  BENCHMARK_JOB_TYPE_LABEL,
} from "@/config/data";
import type { ListJobsQuery } from "@/hooks/useFetchNextPlayModeJob";
import type { NoTaskAvailableState } from "@/types";
import { markNoTaskJob, readNoTaskJobIds } from "@/utils/no-task-jobs";
import { cn } from "@/utils/utils";

export const JobsTable: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params
  const activeTab = searchParams.get("tab") ?? "confirmation";
  const initialStore = searchParams.get("store") || null;
  const initialPage = Number(searchParams.get("page")) || 1;
  const initialDateRange: [Date | null, Date | null] = (() => {
    const start = searchParams.get("dateStart");
    const end = searchParams.get("dateEnd");
    if (start && end) {
      const startDate = new Date(start);
      const endDate = new Date(end);
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        return [startDate, endDate];
      }
    }
    return [null, null];
  })();

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [confirmationJobs, setConfirmationJobs] = useState<Job[]>([]);
  const [confirmationQaJobs, setConfirmationQaJobs] = useState<Job[]>([]);
  const [confirmationJobsAdmin, setConfirmationJobsAdmin] = useState<Job[]>([]);
  const [confirmationJobCount, setConfirmationJobCount] = useState<number>(0);
  const [confirmationQaJobCount, setConfirmationQaJobCount] =
    useState<number>(0);
  const [confirmationJobsAdminCount, setConfirmationJobsAdminCount] =
    useState<number>(0);
  const [coverageJobs, setCoverageJobs] = useState<Job[]>([]);
  const [coverageJobCount, setCoverageJobCount] = useState<number>(0);
  const [coverageBetaJobs, setCoverageBetaJobs] = useState<Job[]>([]);
  const [coverageBetaJobCount, setCoverageBetaJobCount] = useState<number>(0);
  const [coverageJobsAdmin, setCoverageJobsAdmin] = useState<Job[]>([]);
  const [coverageJobsAdminCount, setCoverageJobsAdminCount] =
    useState<number>(0);
  const [coverageBetaJobsAdmin, setCoverageBetaJobsAdmin] = useState<Job[]>([]);
  const [coverageBetaJobsAdminCount, setCoverageBetaJobsAdminCount] =
    useState<number>(0);
  const [coverageQaJobs, setCoverageQaJobs] = useState<Job[]>([]);
  const [coverageQaJobCount, setCoverageQaJobCount] = useState<number>(0);
  const [coverageQaJobsAdmin, setCoverageQaJobsAdmin] = useState<Job[]>([]);
  const [coverageQaJobsAdminCount, setCoverageQaJobsAdminCount] =
    useState<number>(0);
  const [coverageBetaQaJobs, setCoverageBetaQaJobs] = useState<Job[]>([]);
  const [coverageBetaQaJobCount, setCoverageBetaQaJobCount] =
    useState<number>(0);
  const [coverageBetaQaJobsAdmin, setCoverageBetaQaJobsAdmin] = useState<Job[]>(
    [],
  );
  const [coverageBetaQaJobsAdminCount, setCoverageBetaQaJobsAdminCount] =
    useState<number>(0);
  const [oosBenchmarkJobs, setOosBenchmarkJobs] = useState<Job[]>([]);
  const [oosBenchmarkJobCount, setOosBenchmarkJobCount] = useState<number>(0);
  const [coverageBenchmarkJobs, setCoverageBenchmarkJobs] = useState<Job[]>([]);
  const [coverageBenchmarkJobCount, setCoverageBenchmarkJobCount] =
    useState<number>(0);
  // Merged, newest first. Each family's own page is independently sized, so
  // this is a reasonable blend rather than a true single-cursor pagination —
  // acceptable for an admin-only management view at modest quiz volume.
  const benchmarkJobs: Job[] = useMemo(
    () =>
      [...oosBenchmarkJobs, ...coverageBenchmarkJobs].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
    [oosBenchmarkJobs, coverageBenchmarkJobs],
  );
  const benchmarkJobCount = oosBenchmarkJobCount + coverageBenchmarkJobCount;
  const [authReady, setAuthReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentJobListResponse, setCurrentJobListResponse] =
    useState<any>(null);
  const [stores, setStores] = useState<AtlasStoreSchema[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(
    initialStore,
  );
  const [dateRange, setDateRange] =
    useState<[Date | null, Date | null]>(initialDateRange);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null); // eslint-disable-line
  const fetchAbortRef = useRef<AbortController | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Set when a task page bounced back here because the job had nothing to
  // serve (e.g. someone else just finished its last task). Copied out of the
  // router state so later search-param syncs don't drop it.
  const [noTaskNotice, setNoTaskNotice] = useState<NoTaskAvailableState | null>(
    () => {
      const state = location.state as NoTaskAvailableState | null;
      return state?.noTaskAvailableJobId ? state : null;
    },
  );
  // Jobs bounced out of recently. lebron still lists them (it lists every
  // in-flight job, claimable by this user or not), so their rows are marked.
  const [noTaskJobIds, setNoTaskJobIds] = useState(readNoTaskJobIds);
  // Remember the bounced job so its row stays marked across polls, then drop
  // the router state so a refresh doesn't replay the notice.
  useEffect(() => {
    if (!noTaskNotice) return;
    markNoTaskJob(noTaskNotice.noTaskAvailableJobId);
    setNoTaskJobIds(readNoTaskJobIds());
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
    // Mount-only: the notice is captured once from the arriving router state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [playModeLoading, setPlayModeLoading] = useState(false);
  const dispatch = useAppDispatch();
  const isPlayMode = useAppSelector((state) => state.oos.isPlayMode);

  // Clear play mode whenever the jobs table is rendered — catches browser back,
  // ExitModal, and any navigation path that doesn't explicitly stop play mode.
  useEffect(() => {
    dispatch(setIsPlayMode(false));
    dispatch(setTaskPlaylist([]));
  }, [dispatch]);
  const selectedRetailerId = useAppSelector(
    (state) => state.oos.selectedRetailerId,
  );

  const handlePlayMode = async () => {
    if (isPlayMode) {
      dispatch(setIsPlayMode(false));
      return;
    }

    // "My Tasks" play mode is task-driven: build a queue from the user's pending
    // tasks and walk through them job-by-job (each job holds the assigned task).
    if (activeTab === "my-tasks") {
      setPlayModeLoading(true);
      try {
        const playlist: { jobId: string; taskId: string; route: string }[] = [];
        let page = 1;
        let totalPages = 1;
        do {
          const resp = await mvpApiListAllJobTasks({
            query: {
              assignee: "me",
              status: JobTaskStatus.PENDING,
              ordering: "created_at",
              page,
              page_size: 100,
            },
          });
          const data = resp.data as any;
          for (const t of data?.tasks ?? []) {
            playlist.push({
              jobId: t.job_id,
              taskId: t.id,
              route: TASK_TYPE_ROUTE[t.type] ?? "confirmation",
            });
          }
          totalPages = data?.total_pages ?? 1;
          page += 1;
        } while (page <= totalPages);

        if (playlist.length > 0) {
          dispatch(setTaskPlaylist(playlist));
          dispatch(setIsPlayMode(true));
          const first = playlist[0];
          navigate(
            `/jobs/${first.jobId}/${first.route}?taskId=${first.taskId}`,
          );
        }
      } catch (e) {
        console.error("Failed to start My Tasks play mode:", e);
      } finally {
        setPlayModeLoading(false);
      }
      return;
    }

    const config = PLAY_MODE_JOB_TYPE[activeTab];
    if (!config) return;
    setPlayModeLoading(true);
    try {
      const response = await mvpApiListJobs({
        query: {
          type: config.jobType,
          status: [JobStatus.NEEDS_REVIEW],
          ordering: "created_at",
          page_size: 10,
          page: 1,
          // Entering play mode, so skip jobs holding a task this reviewer
          // must not be served (see useFetchNextPlayModeJob).
          servable_to_me: true,
          ...(selectedRetailerId
            ? { atlas_retailer_id: selectedRetailerId }
            : {}),
        } satisfies ListJobsQuery,
      });
      const jobs = (response.data as any)?.jobs ?? [];
      if (jobs.length > 0) {
        dispatch(setIsPlayMode(true));
        navigate(`/jobs/${jobs[0].id}/${config.route}`);
      }
    } catch (e) {
      console.error("Failed to start play mode:", e);
    } finally {
      setPlayModeLoading(false);
    }
  };

  // Helper to sync filter state to URL search params
  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === null || value === undefined) {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const { value: isAdmin, isLoading: statsigLoading } =
    useGate("out_of_stock_admin");
  const { value: showPlayButton } = useGate("show_play_button");
  const { value: benchmarkEnabled } = useGate("fe_enable_oos_benchmark_quiz");

  // The lebron client only attaches a Firebase bearer token once a user is
  // signed in, so identity-scoped calls have to wait for auth to settle.
  useEffect(() => {
    getCurrentUser().then((user) => setAuthReady(!!user));
  }, []);

  // "My Quizzes" reads the identity-scoped endpoint (every status) for anyone
  // with the gate — admins included, so their own quizzes are visible too.
  // "Manage Quizzes" (admin) keeps the all-reviewers /jobs-in-flight list.
  const { data: myQuizzes, isLoading: myQuizzesLoading } =
    useMyBenchmarkQuizzes(benchmarkEnabled && authReady);

  const myQuizJobs: Job[] = useMemo(
    () =>
      (myQuizzes ?? []).map(
        (quiz) =>
          // A quiz job carries no store worth showing (they're all pinned to the
          // benchmark test store), so the table labels that cell instead.
          ({
            id: quiz.id,
            type: quiz.type,
            status: quiz.status,
            created_at: quiz.created_at,
          }) as Job,
      ),
    [myQuizzes],
  );

  // The quiz whose results modal is open. Local state, not the Redux
  // `activeModal` slice — that slice is scoped to the task pages. The modal owns
  // the fetch, so this id is all the page has to hold.
  const [openQuizId, setOpenQuizId] = useState<string | null>(null);

  const pageSize = 100;
  const msPolling = 10000; // 10 seconds

  // Get current jobs based on active tab
  const getCurrentJobs = () => {
    switch (activeTab) {
      case "confirmation-qa-feedback":
      case "my-tasks":
        return [];
      case "my-quizzes":
        return myQuizJobs;
      case "benchmark-admin":
        return benchmarkJobs;
      case "confirmation-qa":
        return confirmationQaJobs;
      case "admin-confirmation":
        return confirmationJobsAdmin;
      case "coverage":
        return coverageJobs;
      case "coverage-beta":
        return coverageBetaJobs;
      case "coverage-qa":
        return coverageQaJobs;
      case "coverage-beta-qa":
        return coverageBetaQaJobs;
      case "admin-coverage":
        return coverageJobsAdmin;
      case "admin-coverage-beta":
        return coverageBetaJobsAdmin;
      case "admin-coverage-qa":
        return coverageQaJobsAdmin;
      case "admin-coverage-beta-qa":
        return coverageBetaQaJobsAdmin;
      default: // "confirmation"
        return confirmationJobs;
    }
  };

  const currentJobs = getCurrentJobs();

  // Fetch jobs by a specific job type
  const fetchByJobType = useCallback(
    async (
      page: number,
      storeId: string | null,
      jobType:
        | "confirmation"
        | "confirmation-benchmark"
        | "confirmation-qa"
        | "coverage"
        | "coverage-beta"
        | "coverage-qa"
        | "coverage-beta-qa"
        | "coverage-benchmark",
      dateRange: [Date | null, Date | null] = [null, null],
      signal?: AbortSignal,
    ) => {
      let type;
      if (jobType === "confirmation") {
        type = JobType.OUT_OF_STOCK_CONFIRMATION_TASKS;
      } else if (jobType === "confirmation-benchmark") {
        // String literal until @augmodo/lebron-browser-client picks up the enum.
        type = "OUT_OF_STOCK_CONFIRMATION_BENCHMARK_TASKS";
      } else if (jobType === "confirmation-qa") {
        type = JobType.OUT_OF_STOCK_CONFIRMATION_QA_TASKS;
      } else if (jobType === "coverage") {
        type = JobType.COVERAGE_REVIEW_TASKS;
      } else if (jobType === "coverage-beta") {
        type = JobType.COVERAGE_REVIEW_TASKS;
      } else if (jobType === "coverage-qa") {
        type = JobType.COVERAGE_REVIEW_QA_TASKS;
      } else if (jobType === "coverage-beta-qa") {
        // String literal until @augmodo/lebron-browser-client picks up the enum.
        type = "COVERAGE_REVIEW_BETA_QA_TASKS";
      } else if (jobType === "coverage-benchmark") {
        // TODO: String literal until @augmodo/lebron-browser-client picks up the enum.
        type = "COVERAGE_REVIEW_BENCHMARK_TASKS";
      }
      const queryParams: any = {
        type,
        page_size: pageSize,
        page: page,
      };

      // Add date range filters if provided (admin only)
      if (isAdmin && dateRange[0] && dateRange[1]) {
        queryParams.created_after = dateRange[0].toISOString();
        queryParams.created_before = dateRange[1].toISOString();
      } else if (!isAdmin) {
        queryParams.created_after = "2025-01-01T00:00:00Z"; // TODO: make this dynamic
      }

      // Add store filter if selected
      if (storeId) {
        queryParams.store_id = storeId;
      }
      if (selectedRetailerId) {
        queryParams.atlas_retailer_id = selectedRetailerId;
      }

      const response = await mvpApiListJobsInFlight({
        query: queryParams,
        signal,
        throwOnError: true,
      });

      const listJobsResponse = response.data;

      // Transform API jobs to our Job interface
      const transformedJobs = listJobsResponse.jobs.map((apiJob: any) => ({
        id: apiJob.id,
        type: apiJob.type,
        store: apiJob.store,
        status: apiJob.status || JobStatus.NEEDS_REVIEW,
        created_at: apiJob.created_at || new Date().toISOString(),
        testing: apiJob.testing,
      }));

      return {
        jobs: transformedJobs,
        count: listJobsResponse.count,
        total_pages: listJobsResponse.total_pages,
        listJobsResponse,
      };
    },
    [pageSize, isAdmin, selectedRetailerId],
  );

  const fetchJobsOfAllStatus = useCallback(
    async (
      page: number,
      storeId: string | null,
      jobType:
        | "confirmation"
        | "confirmation-qa"
        | "coverage"
        | "coverage-qa"
        | "coverage-beta-qa",
      dateRange: [Date | null, Date | null] = [null, null],
      signal?: AbortSignal,
    ) => {
      if (!isAdmin) return null;
      let type: JobType | string = JobType.OUT_OF_STOCK_CONFIRMATION_TASKS;
      if (jobType === "confirmation-qa") {
        type = JobType.OUT_OF_STOCK_CONFIRMATION_QA_TASKS;
      } else if (jobType === "coverage") {
        type = JobType.COVERAGE_REVIEW_TASKS;
      } else if (jobType === "coverage-qa") {
        type = JobType.COVERAGE_REVIEW_QA_TASKS;
      } else if (jobType === "coverage-beta-qa") {
        // String literal until @augmodo/lebron-browser-client picks up the enum.
        type = "COVERAGE_REVIEW_BETA_QA_TASKS";
      }
      const queryParams: any = {
        page_size: pageSize,
        page: page,
        status: [
          JobStatus.NEEDS_REVIEW,
          JobStatus.IN_PROGRESS,
          JobStatus.COMPLETED,
        ],
        type,
      };
      // default date range to 7 days ago
      if (dateRange[0] && dateRange[1]) {
        queryParams.created_after = dateRange[0].toISOString();
        queryParams.created_before = dateRange[1].toISOString();
      }

      // Add store filter if selected
      if (storeId) {
        queryParams.store_id = storeId;
      }
      if (selectedRetailerId) {
        queryParams.atlas_retailer_id = selectedRetailerId;
      }

      const response = await mvpApiListJobs({
        query: queryParams,
        signal,
        throwOnError: true,
      });

      const listJobsResponse = response.data;

      // Transform API jobs to our Job interface
      const transformedJobs = listJobsResponse.jobs.map((apiJob: any) => ({
        id: apiJob.id,
        type: apiJob.type,
        store: apiJob.store,
        status: apiJob.status,
        created_at: apiJob.created_at || new Date().toISOString(),
        testing: apiJob.testing,
      }));

      return {
        jobs: transformedJobs,
        count: listJobsResponse.count,
        total_pages: listJobsResponse.total_pages,
        listJobsResponse,
      };
    },
    [pageSize, isAdmin, selectedRetailerId],
  );

  // Fetch both job types simultaneously
  const fetchAllJobs = useCallback(
    (
      page: number,
      storeId: string | null,
      dateRange: [Date | null, Date | null] = [null, null],
    ) => {
      if (
        activeTab === "confirmation-qa-feedback" ||
        activeTab === "my-tasks"
      ) {
        // These tabs fetch their own data (QaFeedbackTabContent /
        // MyTasksTabContent) — clear the table's loading flag so the
        // LoadingOverlay doesn't hang forever.
        setLoading(false);
        return;
      }

      // Abort any in-flight fetch so stale responses don't overwrite newer ones
      fetchAbortRef.current?.abort();
      const controller = new AbortController();
      fetchAbortRef.current = controller;
      const { signal } = controller;

      setLoading(true);

      // Capture the active tab at call time so closures below stay consistent.
      const tab = activeTab;

      // Each fetch updates its own state slice when it resolves. The active
      // tab's fetch also clears the loading overlay so a slow background tab
      // can't block the current view.
      const wire = (
        promise: Promise<any>,
        onSuccess: (data: any) => void,
        label: string,
        isActiveTab: boolean,
      ) => {
        promise
          .then((data) => {
            if (signal.aborted) return;
            if (data != null) onSuccess(data);
            if (isActiveTab) setLoading(false);
          })
          .catch((e) => {
            if (signal.aborted) return;
            console.error(`Failed to fetch ${label} jobs:`, e);
            if (isActiveTab) setLoading(false);
          });
      };

      wire(
        fetchByJobType(page, storeId, "confirmation", dateRange, signal),
        (d) => {
          setConfirmationJobs(d.jobs);
          setConfirmationJobCount(d.count);
          if (tab === "confirmation") setCurrentJobListResponse(d);
        },
        "confirmation",
        tab === "confirmation",
      );

      wire(
        fetchJobsOfAllStatus(page, storeId, "confirmation", dateRange, signal),
        (d) => {
          setConfirmationJobsAdmin(d.jobs);
          setConfirmationJobsAdminCount(d.count);
          if (tab === "admin-confirmation") setCurrentJobListResponse(d);
        },
        "admin-confirmation",
        tab === "admin-confirmation",
      );

      wire(
        fetchByJobType(page, storeId, "confirmation-qa", dateRange, signal),
        (d) => {
          setConfirmationQaJobs(d.jobs);
          setConfirmationQaJobCount(d.count);
          if (tab === "confirmation-qa") setCurrentJobListResponse(d);
        },
        "confirmation-qa",
        tab === "confirmation-qa",
      );

      // "Manage Quizzes" (admin): every reviewer's quizzes, across every
      // benchmark family. Fetched only when the gate is on (the job types
      // don't exist server-side until the lebron enum deploys). "My Quizzes"
      // doesn't come through here at all — it reads /qc-benchmarks/jobs/mine
      // via useMyBenchmarkQuizzes.
      if (benchmarkEnabled && isAdmin) {
        // Both families' own fetches clear the loading overlay independently
        // when this is the active tab (same as every other tab above) — the
        // merged list can briefly show just one family until the other
        // lands, which is an acceptable rough edge for an admin-only view.
        wire(
          fetchByJobType(
            page,
            storeId,
            "confirmation-benchmark",
            dateRange,
            signal,
          ),
          (d) => {
            setOosBenchmarkJobs(d.jobs);
            setOosBenchmarkJobCount(d.count);
          },
          "confirmation-benchmark",
          tab === "benchmark-admin",
        );
        wire(
          fetchByJobType(
            page,
            storeId,
            "coverage-benchmark",
            dateRange,
            signal,
          ),
          (d) => {
            setCoverageBenchmarkJobs(d.jobs);
            setCoverageBenchmarkJobCount(d.count);
          },
          "coverage-benchmark",
          tab === "benchmark-admin",
        );
      } else if (tab === "benchmark-admin") {
        // No table fetch owns this tab (gate off) — clear the overlay so it
        // doesn't hang forever.
        setOosBenchmarkJobs([]);
        setOosBenchmarkJobCount(0);
        setCoverageBenchmarkJobs([]);
        setCoverageBenchmarkJobCount(0);
        setLoading(false);
      }
      if (tab === "my-quizzes") {
        // The my-quizzes list is a react-query hook with its own loading state,
        // not one of the table's fetches.
        setLoading(false);
      }

      wire(
        fetchByJobType(page, storeId, "coverage", dateRange, signal),
        (d) => {
          setCoverageJobs(d.jobs);
          setCoverageJobCount(d.count);
          if (tab === "coverage") setCurrentJobListResponse(d);
        },
        "coverage",
        tab === "coverage",
      );

      wire(
        fetchByJobType(page, storeId, "coverage-beta", dateRange, signal),
        (d) => {
          setCoverageBetaJobs(d.jobs);
          setCoverageBetaJobCount(d.count);
          if (tab === "coverage-beta") {
            setCurrentJobListResponse(d);
          }
        },
        "coverage-beta",
        tab === "coverage-beta",
      );

      wire(
        fetchJobsOfAllStatus(page, storeId, "coverage", dateRange, signal),
        (d) => {
          // Client-side filter: /v1/jobs may return mixed types for coverage
          const filtered = d.jobs.filter(
            (j: any) => j.type === JobType.COVERAGE_REVIEW_TASKS,
          );
          setCoverageJobsAdmin(filtered);
          setCoverageJobsAdminCount(d.count);
          if (tab === "admin-coverage") setCurrentJobListResponse(d);
        },
        "admin-coverage",
        tab === "admin-coverage",
      );

      wire(
        fetchJobsOfAllStatus(page, storeId, "coverage", dateRange, signal),
        (d) => {
          // Client-side filter: /v1/jobs may return mixed types for coverage
          const filtered = d.jobs.filter(
            (j: any) => j.type === JobType.COVERAGE_REVIEW_TASKS,
          );
          setCoverageBetaJobsAdmin(filtered);
          setCoverageBetaJobsAdminCount(d.count);
          if (tab === "admin-coverage-beta") setCurrentJobListResponse(d);
        },
        "admin-coverage-beta",
        tab === "admin-coverage-beta",
      );

      wire(
        fetchByJobType(page, storeId, "coverage-qa", dateRange, signal),
        (d) => {
          setCoverageQaJobs(d.jobs);
          setCoverageQaJobCount(d.count);
          if (tab === "coverage-qa") setCurrentJobListResponse(d);
        },
        "coverage-qa",
        tab === "coverage-qa",
      );

      wire(
        fetchJobsOfAllStatus(page, storeId, "coverage-qa", dateRange, signal),
        (d) => {
          const filtered = d.jobs.filter(
            (j: any) => j.type === JobType.COVERAGE_REVIEW_QA_TASKS,
          );
          setCoverageQaJobsAdmin(filtered);
          setCoverageQaJobsAdminCount(d.count);
          if (tab === "admin-coverage-qa") setCurrentJobListResponse(d);
        },
        "admin-coverage-qa",
        tab === "admin-coverage-qa",
      );

      wire(
        fetchByJobType(page, storeId, "coverage-beta-qa", dateRange, signal),
        (d) => {
          setCoverageBetaQaJobs(d.jobs);
          setCoverageBetaQaJobCount(d.count);
          if (tab === "coverage-beta-qa") setCurrentJobListResponse(d);
        },
        "coverage-beta-qa",
        tab === "coverage-beta-qa",
      );

      wire(
        fetchJobsOfAllStatus(
          page,
          storeId,
          "coverage-beta-qa",
          dateRange,
          signal,
        ),
        (d) => {
          setCoverageBetaQaJobsAdmin(d.jobs);
          setCoverageBetaQaJobsAdminCount(d.count);
          if (tab === "admin-coverage-beta-qa") setCurrentJobListResponse(d);
        },
        "admin-coverage-beta-qa",
        tab === "admin-coverage-beta-qa",
      );
    },
    [
      fetchByJobType,
      fetchJobsOfAllStatus,
      activeTab,
      benchmarkEnabled,
      isAdmin,
    ],
  );

  // Fetch stores for filter dropdown
  const fetchStores = useCallback(async () => {
    try {
      const storesResponse = await mvpApiListStores({
        query: {
          page_size: 1000,
          page: 1,
          ...(selectedRetailerId ? { retailer_id: selectedRetailerId } : {}),
        },
        throwOnError: true,
      });

      setStores(storesResponse.data.stores);
    } catch (e) {
      console.error("Failed to fetch stores:", e);
    }
  }, [selectedRetailerId]);

  const setupPolling = useCallback(() => {
    // Clear existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    // Set up new polling interval - only fetch current page
    pollIntervalRef.current = setInterval(() => {
      fetchAllJobs(currentPage, selectedStoreId, dateRange);
      // Expire bounced-job marks on the same beat.
      setNoTaskJobIds(readNoTaskJobIds());
    }, msPolling);
  }, [fetchAllJobs, currentPage, msPolling, selectedStoreId, dateRange]);

  // Initial load
  useEffect(() => {
    // For admin users, wait until date range is set before initial load
    if (
      statsigLoading ||
      (isAdmin && dateRange[0] === null && dateRange[1] === null)
    ) {
      return; // Skip initial load, will load after date range is set
    }
    // Skip when only one date is selected (user is still picking the range)
    if (dateRange[0] && !dateRange[1]) {
      return;
    }
    fetchAllJobs(currentPage, selectedStoreId, dateRange);
  }, [fetchAllJobs, dateRange, isAdmin, statsigLoading]);

  // Set default date range for admin users (skip if URL already has date params)
  useEffect(() => {
    const hasDateParams =
      searchParams.has("dateStart") && searchParams.has("dateEnd");
    if (
      isAdmin &&
      dateRange[0] === null &&
      dateRange[1] === null &&
      !hasDateParams
    ) {
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 1);
      // Set end date to end of today to include jobs created today
      endDate.setHours(23, 59, 59, 999);
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);
      // Set start date to beginning of the day 7 days ago
      startDate.setHours(0, 0, 0, 0);
      setDateRange([startDate, endDate]);
      // The initial load effect will trigger after dateRange is updated
    }
  }, [isAdmin]);

  // Setup polling
  useEffect(() => {
    setupPolling();

    // Cleanup on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [setupPolling]);

  // Fetch stores (re-runs when selectedRetailerId changes via fetchStores dep)
  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  // Reset selected store and immediately refetch when retailer changes.
  // We call fetchAllJobs(1, null, ...) directly here rather than relying on
  // the initial-load effect, because setSelectedStoreId(null) only takes
  // effect in the next render — by then the initial-load effect won't re-fire
  // (fetchAllJobs identity doesn't change between those two renders).
  const isFirstRetailerRender = useRef(true);
  useEffect(() => {
    if (isFirstRetailerRender.current) {
      isFirstRetailerRender.current = false;
      return;
    }
    setSelectedStoreId(null);
    setCurrentPage(1);
    updateSearchParams({ store: null, page: null });
    fetchAllJobs(1, null, dateRange);
  }, [selectedRetailerId]);

  const totalPages = currentJobListResponse?.total_pages || 1;

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      updateSearchParams({ page: page === 1 ? null : String(page) });
      fetchAllJobs(page, selectedStoreId, dateRange);
    },
    [fetchAllJobs, selectedStoreId, dateRange, updateSearchParams],
  );

  const handleStoreFilterChange = useCallback(
    (storeId: string | null) => {
      setSelectedStoreId(storeId);
      setCurrentPage(1); // Reset to first page when filter changes
      updateSearchParams({ store: storeId, page: null });
      fetchAllJobs(1, storeId, dateRange);
    },
    [fetchAllJobs, dateRange, updateSearchParams],
  );

  const handleDateRangeChange = useCallback(
    (value: [Date | null, Date | null]) => {
      setDateRange(value);

      // Only refetch if both start and end dates are selected, or if clearing the range
      if ((value[0] && value[1]) || (!value[0] && !value[1])) {
        setCurrentPage(1); // Reset to first page when filter changes
        updateSearchParams({
          dateStart: value[0] ? value[0].toISOString() : null,
          dateEnd: value[1] ? value[1].toISOString() : null,
          page: null,
        });
        fetchAllJobs(1, selectedStoreId, value);
      }
    },
    [fetchAllJobs, selectedStoreId, updateSearchParams],
  );

  // When the sidebar changes the active tab via URL, reset pagination and sync the
  // current job list from cached data so the table updates without waiting for a refetch.
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "confirmation-qa-feedback" || activeTab === "my-tasks")
      return;

    let jobs: Job[];
    let count: number;

    switch (activeTab) {
      case "my-quizzes":
        jobs = myQuizJobs;
        count = myQuizJobs.length;
        break;
      case "benchmark-admin":
        jobs = benchmarkJobs;
        count = benchmarkJobCount;
        break;
      case "confirmation-qa":
        jobs = confirmationQaJobs;
        count = confirmationQaJobCount;
        break;
      case "admin-confirmation":
        jobs = confirmationJobsAdmin;
        count = confirmationJobsAdminCount;
        break;
      case "coverage":
        jobs = coverageJobs;
        count = coverageJobCount;
        break;
      case "coverage-beta":
        jobs = coverageBetaJobs;
        count = coverageBetaJobCount;
        break;
      case "coverage-qa":
        jobs = coverageQaJobs;
        count = coverageQaJobCount;
        break;
      case "coverage-beta-qa":
        jobs = coverageBetaQaJobs;
        count = coverageBetaQaJobCount;
        break;
      case "admin-coverage":
        jobs = coverageJobsAdmin;
        count = coverageJobsAdminCount;
        break;
      case "admin-coverage-beta":
        jobs = coverageBetaJobsAdmin;
        count = coverageBetaJobsAdminCount;
        break;
      case "admin-coverage-qa":
        jobs = coverageQaJobsAdmin;
        count = coverageQaJobsAdminCount;
        break;
      case "admin-coverage-beta-qa":
        jobs = coverageBetaQaJobsAdmin;
        count = coverageBetaQaJobsAdminCount;
        break;
      default:
        jobs = confirmationJobs;
        count = confirmationJobCount;
        break;
    }

    if (jobs.length > 0) {
      setCurrentJobListResponse({
        jobs,
        count,
        total_pages: Math.ceil(count / pageSize),
      });
    }
  }, [
    activeTab,
    confirmationJobs,
    confirmationJobCount,
    benchmarkJobs,
    benchmarkJobCount,
    myQuizJobs,
    confirmationQaJobs,
    confirmationQaJobCount,
    confirmationJobsAdmin,
    confirmationJobsAdminCount,
    coverageJobs,
    coverageJobCount,
    coverageQaJobs,
    coverageQaJobCount,
    coverageJobsAdmin,
    coverageJobsAdminCount,
    coverageQaJobsAdmin,
    coverageQaJobsAdminCount,
    coverageBetaQaJobs,
    coverageBetaQaJobCount,
    coverageBetaQaJobsAdmin,
    coverageBetaQaJobsAdminCount,
    pageSize,
  ]);

  // A benchmark-tab row's route depends on the quiz's OWN family (OOS
  // confirmation vs. coverage review) — "My Quizzes"/"Manage Quizzes" can
  // list both in one table, so this is resolved per row, not once per tab.
  const isBenchmarkTab =
    activeTab === "my-quizzes" || activeTab === "benchmark-admin";
  const routeForJob = useCallback(
    (job: Job): string =>
      isBenchmarkTab
        ? (BENCHMARK_JOB_TYPE_ROUTE[job.type ?? ""] ?? "confirmation")
        : activeTab,
    [isBenchmarkTab, activeTab],
  );

  const handleRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>, job: Job) => {
      const to = `/jobs/${job.id}/${routeForJob(job)}`;
      if (e.metaKey || e.ctrlKey) {
        window.open(to, "_blank");
      } else {
        navigate(to);
      }
    },
    [navigate, routeForJob],
  );

  // A finished quiz has no pending task left, so next-pending-task would land
  // the reviewer on the confirmation page's "No task found" state. Its row opens
  // the results modal instead of linking into that dead end — the id stays plain
  // text because the row click, not a link, is what does something useful.
  const isFinishedQuiz = (job: Job) =>
    isBenchmarkTab && job.status === JobStatus.COMPLETED;

  // The my-quizzes list is a react-query hook, not one of the table's own
  // fetches, so it carries its own loading state.
  const tableLoading =
    loading || (activeTab === "my-quizzes" && (!authReady || myQuizzesLoading));

  const rows = currentJobs.map((job) => (
    <Table.Tr
      key={job.id}
      onClick={(e) => {
        if (isFinishedQuiz(job)) setOpenQuizId(job.id);
        else handleRowClick(e, job);
      }}
      style={{ cursor: "pointer" }}
      className={cn(
        "hover:bg-gray-50 transition-colors",
        noTaskJobIds.has(job.id) && "opacity-50",
      )}
    >
      <Table.Td>
        <Group gap="xs">
          <Text fw={600} size="sm">
            {isFinishedQuiz(job) ? (
              job.id
            ) : (
              <Link
                to={`/jobs/${job.id}/${routeForJob(job)}`}
                style={{ textDecoration: "underline" }}
                onClick={(e) => e.stopPropagation()}
              >
                {job.id}
              </Link>
            )}
          </Text>
          {job.testing && (
            <Badge color="yellow" variant="light" size="sm">
              Testing
            </Badge>
          )}
          <Text c="dimmed" size="sm">
            {job.store?.name ||
              (isBenchmarkTab
                ? (BENCHMARK_JOB_TYPE_LABEL[job.type ?? ""] ?? "Benchmark quiz")
                : "Unknown Store")}
          </Text>
        </Group>
      </Table.Td>
      <Table.Td>
        <Badge color={getStatusColor(job.status)} variant="light" size="sm">
          {getStatusText(job.status)}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{new Date(job.created_at).toLocaleString()}</Text>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <div className="space-y-6 w-full h-full p-6">
      {noTaskNotice?.noTaskReason === "finished" && (
        <Alert
          color="green"
          icon={<IconCircleCheck size={16} />}
          title="Job finished"
          withCloseButton
          onClose={() => setNoTaskNotice(null)}
        >
          You've worked through every task you can take in{" "}
          {noTaskNotice.noTaskAvailableJobId}. Pick another job.
        </Alert>
      )}
      {noTaskNotice && noTaskNotice.noTaskReason !== "finished" && (
        <Alert
          color="yellow"
          icon={<IconAlertCircle size={16} />}
          title="No tasks available"
          withCloseButton
          onClose={() => setNoTaskNotice(null)}
        >
          {noTaskNotice.noTaskAvailableJobId} has no tasks you can open right
          now. Someone else may have just finished or picked them up. Pick
          another job.
        </Alert>
      )}

      {/* Filter Section */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-3">
          <IconAdjustments />
          <Text size="md" fw={600} c="gray.8">
            Filters
          </Text>
        </div>

        <div className="flex flex-row gap-4 items-end">
          <div className="space-y-1 text-left">
            <Text size="sm" fw={500} c="gray.7" className="text-left">
              Store
            </Text>
            <Select
              placeholder="Select a store to filter"
              value={selectedStoreId}
              onChange={handleStoreFilterChange}
              data={stores.map((store) => ({
                value: store.lebron_id || store.id,
                label: store.name,
              }))}
              clearable
              size="sm"
              searchable
              w={300}
              disabled={activeTab === "confirmation-qa-feedback"}
              styles={{
                input: {
                  borderColor: "#e2e8f0",
                  "&:focus": {
                    borderColor: "#3b82f6",
                  },
                },
              }}
            />
          </div>
          {isAdmin && (
            <div className="space-y-1 text-left">
              <Text size="sm" fw={500} c="gray.7" className="text-left">
                Date Range
              </Text>
              <DatePickerInput
                type="range"
                placeholder="Select date range"
                value={dateRange}
                onChange={handleDateRangeChange}
                leftSection={<IconCalendar size={16} />}
                clearable
                size="sm"
                w={300}
                valueFormat="MMM DD, YYYY"
                disabled={
                  activeTab === "confirmation-qa-feedback" ||
                  activeTab === "my-tasks"
                }
                styles={{
                  input: {
                    borderColor: "#e2e8f0",
                    "&:focus": {
                      borderColor: "#3b82f6",
                    },
                  },
                }}
              />
            </div>
          )}
          {PLAY_MODE_TABS.has(activeTab) &&
            (showPlayButton || activeTab === "my-tasks") && (
              <Button
                className="ml-auto"
                size="sm"
                variant={isPlayMode ? "filled" : "light"}
                color="blue"
                leftSection={<IconPlayerPlay size={14} />}
                loading={playModeLoading}
                onClick={handlePlayMode}
              >
                Play Mode
              </Button>
            )}
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm w-full h-full relative flex flex-col">
        <LoadingOverlay visible={tableLoading} zIndex={100} />

        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">
            {activeTab === "my-tasks"
              ? "My Tasks"
              : `Jobs${currentJobListResponse?.count != null ? ` (${currentJobListResponse.count})` : ""}`}
          </h2>
        </div>

        {activeTab === "benchmark-admin" && benchmarkEnabled && isAdmin && (
          <BenchmarkQuizCreateForm />
        )}

        {/* empty state */}
        {!rows.length &&
          !tableLoading &&
          activeTab !== "confirmation-qa-feedback" &&
          activeTab !== "my-tasks" && (
            <div className="flex-1 min-h-0 overflow-y-scroll">
              <Text>No jobs found</Text>
            </div>
          )}

        {activeTab === "confirmation-qa-feedback" && <QaFeedbackTabContent />}

        {activeTab === "my-tasks" && (
          <div className="flex-1 min-h-0 overflow-y-scroll">
            <MyTasksTabContent />
          </div>
        )}

        {rows.length > 0 &&
          activeTab !== "confirmation-qa-feedback" &&
          activeTab !== "my-tasks" && (
            <div className="flex-1 min-h-0 overflow-y-scroll">
              <Table withTableBorder stickyHeader>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Job ID / Store</Table.Th>
                    <Table.Th style={{ textAlign: "center" }}>Status</Table.Th>
                    <Table.Th style={{ textAlign: "center" }}>
                      Created Date
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{rows}</Table.Tbody>
              </Table>
            </div>
          )}

        {activeTab !== "confirmation-qa-feedback" && (
          <div className="flex justify-center mt-6">
            <Pagination
              total={totalPages}
              value={currentPage}
              onChange={handlePageChange}
              size="sm"
            />
          </div>
        )}
      </div>

      <QuizMetricsModal
        quizId={openQuizId}
        onClose={() => setOpenQuizId(null)}
      />
    </div>
  );
};
